import "./LiftFromTvChart.scss";
import { useState, useMemo, useCallback } from "react";
import * as R from "ramda";
import { Brand60, Neutral500 } from "../../utils/colors";
import { LineChart, XAxis, YAxis, Line, Tooltip, CartesianGrid } from "recharts";
import { DownloadDropdown } from "../../Components/DownloadDropdown/DownloadDropdown";
import { Dropdown, DropdownToggleType } from "../../Components/Dropdown/Dropdown";
import { GraphTableToggleButton } from "../../Components/GraphTableToggleButton/GraphTableToggleButton";
import { AxesSkeleton, Skeleton, PathSkeleton } from "../../Components/Skeleton";
import { BPMTable, Header } from "../../Components/StickyTable/BPMTable";
import { downloadPNG, exportToExcel } from "../../utils/download-utils";
import { formatNumberAsInt } from "@blisspointmedia/bpm-types/dist/formatUtils";
import { abbreviateNumber } from "../../utils/data";
import AutoSizer from "react-virtualized-auto-sizer";
import ChartContainer from "../../Components/ChartContainer";
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE,
} from "../../Components/Charts/ChartConstants";
import { DateGroupingKey, DATE_GROUPING_OPTIONS } from "../../CrossChannel/crossChannelFormatters";
import ChartTooltipV2 from "../../Components/Charts/ChartTooltip/ChartTooltipV2";
import { formatDateLabel } from "../../utils/date-utils";
import { CompanyFiscalCal, getGroupingValue } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";

const LINE_STROKE_WIDTH = 2;

interface LiftFromTvData {
  liftFromTv: any[];
  liftModelGradientRanges: any[];
}

interface LiftFromTvChartProps {
  data: LiftFromTvData | undefined;
  showControls?: boolean;
  fiscalCal: CompanyFiscalCal;
}

interface LiftFromTvItem {
  date: string;
  volumeWithTv: number;
  volumeWithoutTv: number;
}

const getDataByDateGrouping = (
  data: LiftFromTvItem[],
  dateGrouping: DateGroupingKey,
  fiscalCal: CompanyFiscalCal
) => {
  let totalsByDate: Record<string, Record<string, number>> = {};
  for (let item of data) {
    const { date, volumeWithTv, volumeWithoutTv } = item;
    const dateToUse = getGroupingValue(date, dateGrouping, fiscalCal);
    totalsByDate = R.mergeDeepRight(totalsByDate, {
      [dateToUse]: {
        volumeWithTv: R.pathOr(0, [dateToUse, "volumeWithTv"], totalsByDate) + volumeWithTv,
        volumeWithoutTv:
          R.pathOr(0, [dateToUse, "volumeWithoutTv"], totalsByDate) + volumeWithoutTv,
      },
    });
  }

  return Object.entries(totalsByDate)
    .map(([date, totals]) => ({ date, ...totals }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const LiftFromTvChart: React.FC<LiftFromTvChartProps> = ({
  data,
  fiscalCal,
  showControls = true,
}) => {
  const { liftFromTv, liftModelGradientRanges } = data || {};
  const [dateGrouping, setDateGrouping] = useState<DateGroupingKey>(DATE_GROUPING_OPTIONS[0].value);
  const [graphView, setGraphView] = useState<boolean>(true);

  const excelDownload = useCallback(() => {
    exportToExcel(liftFromTv || [], "Lift from Linear TV");
  }, [liftFromTv]);

  const pngDownload = useCallback(async () => {
    await downloadPNG(".liftFromTvContainer", "Lift from Linear TV");
  }, []);

  const chartData = useMemo(
    () => getDataByDateGrouping((liftFromTv || []) as LiftFromTvItem[], dateGrouping, fiscalCal),
    [dateGrouping, liftFromTv, fiscalCal]
  );

  const headers = useMemo(() => {
    let headers: Header[] = [
      {
        label: "Date",
        name: "date",
        width: 125,
        nonInteractive: true,
        renderer: item => formatDateLabel(item.date, fiscalCal, "", true) || "-",
      },
      {
        label: "Actual KPI Volume (with TV)",
        name: "volumeWithTv",
        flex: 1,
        renderer: item => formatNumberAsInt(item.volumeWithTv),
      },
      {
        label: "Predicted KPI Volume (w/o TV)",
        name: "volumeWithoutTv",
        flex: 1,
        renderer: item => formatNumberAsInt(item.volumeWithoutTv),
      },
    ];

    return headers;
  }, [fiscalCal]);

  return (
    <div className="liftFromTvContainer">
      <ChartContainer
        enableHoverDesign
        title={
          <>
            <Dropdown
              type={DropdownToggleType.WIDGET_TITLE}
              value={dateGrouping}
              options={DATE_GROUPING_OPTIONS}
              onChange={option => setDateGrouping(option as DateGroupingKey)}
            />
            <div>Lift from Linear TV</div>
          </>
        }
        rightActions={
          showControls && (
            <>
              <GraphTableToggleButton
                graphView={graphView}
                setGraphView={setGraphView}
                widgetName="liftFromTvChart"
              />
              <DownloadDropdown
                size="sm"
                onClickOptions={[excelDownload, pngDownload]}
                disabled={!data}
              />
            </>
          )
        }
      >
        {!data ? (
          <Skeleton>
            <PathSkeleton
              points={[
                [0, 0.3],
                [0.25, 0.7],
                [0.5, 0.2],
                [0.75, 0.9],
                [1, 0.5],
              ]}
              thickness={10}
            />

            <AxesSkeleton size={5} />
          </Skeleton>
        ) : graphView ? (
          <div className="liftFromTvGraphContents">
            {R.isEmpty(chartData) ? (
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "26px",
                }}
              >
                No lift from TV data.
              </div>
            ) : (
              <div className="liftFromTvGraph">
                <AutoSizer>
                  {({ width, height }) => (
                    <LineChart
                      data={chartData}
                      width={width}
                      height={height}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid
                        vertical={false}
                        stroke={CARTESIAN_GRID_STROKE}
                        strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                      />
                      <defs>
                        <linearGradient
                          id="liftChartStrokeGradient1"
                          x1="0%"
                          x2="100%"
                          y1="0%"
                          y2="0%"
                        >
                          {(liftModelGradientRanges || []).map(range => {
                            const { start, end, live } = range;
                            let opacity = live ? 1 : 0.3;
                            return [
                              <stop
                                key={`${start}_${end}_start`}
                                offset={`${start}%`}
                                stopColor={Brand60}
                                stopOpacity={opacity}
                              />,
                              <stop
                                key={`${start}_${end}_end`}
                                offset={`${end}%`}
                                stopColor={Brand60}
                                stopOpacity={opacity}
                              />,
                            ];
                          })}
                        </linearGradient>
                        <linearGradient
                          id="liftChartStrokeGradient2"
                          x1="0%"
                          x2="100%"
                          y1="0%"
                          y2="0%"
                        >
                          {(liftModelGradientRanges || []).map(range => {
                            const { start, end, live } = range;
                            let opacity = live ? 1 : 0.3;
                            return [
                              <stop
                                key={`${start}_${end}_start`}
                                offset={`${start}%`}
                                stopColor={Neutral500}
                                stopOpacity={opacity}
                              />,
                              <stop
                                key={`${start}_${end}_end`}
                                offset={`${end}%`}
                                stopColor={Neutral500}
                                stopOpacity={opacity}
                              />,
                            ];
                          })}
                        </linearGradient>
                      </defs>
                      <YAxis
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickFormatter={number => `${abbreviateNumber(number)}`}
                      />
                      <XAxis
                        dataKey="date"
                        tick={TICK_STYLE}
                        tickFormatter={date => formatDateLabel(date, fiscalCal, dateGrouping)}
                        axisLine={false}
                      />
                      <Line
                        dataKey={"volumeWithTv"}
                        type="monotone"
                        strokeWidth={LINE_STROKE_WIDTH}
                        dot={false}
                        isAnimationActive={false}
                        stroke="url(#liftChartStrokeGradient1)"
                      />
                      <Line
                        dataKey={"volumeWithoutTv"}
                        type="monotone"
                        strokeWidth={LINE_STROKE_WIDTH}
                        dot={false}
                        isAnimationActive={false}
                        stroke="url(#liftChartStrokeGradient2)"
                      />
                      <Tooltip
                        content={({ label, payload }) => (
                          <LiftFromTvTooltip
                            label={label}
                            payload={payload}
                            dateGrouping={dateGrouping}
                            fiscalCal={fiscalCal}
                          />
                        )}
                      />
                    </LineChart>
                  )}
                </AutoSizer>
              </div>
            )}
            <div className="rightOfChart">
              <div className="liftFromTvLegend">
                <div className="legendItem" key="Actual KPI Volume">
                  <div
                    className="line"
                    style={{
                      backgroundColor: Brand60,
                    }}
                  />
                  <div className="legendItemLabel">
                    <div className="primaryText">Actual KPI Volume</div>
                    <div className="subText">(with Linear TV)</div>
                  </div>
                </div>

                <div className="legendItem" key="Predicted KPI Volume">
                  <div
                    className="line"
                    style={{
                      backgroundColor: Neutral500,
                    }}
                  />
                  <div className="legendItemLabel">
                    <div className="primaryText">Predicted KPI Volume</div>
                    <div className="subText">(without Linear TV)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <BPMTable
            headers={headers}
            data={chartData}
            filterBar={false}
            headerHeight={30}
            bottomRowHeight={30}
          />
        )}
      </ChartContainer>
    </div>
  );
};

interface LiftFromTvTooltipProps {
  label: string;
  payload: any[] | undefined;
  dateGrouping: DateGroupingKey;
  fiscalCal: CompanyFiscalCal;
}

const formatTooltipLabel = (label: "volumeWithTv" | "volumeWithoutTv") =>
  label === "volumeWithTv" ? "Actual" : "Predicted";

const getTooltipColor = (label: "volumeWithTv" | "volumeWithoutTv") =>
  label === "volumeWithTv" ? Brand60 : Neutral500;

const LiftFromTvTooltip: React.FC<LiftFromTvTooltipProps> = ({
  label,
  payload,
  dateGrouping,
  fiscalCal,
}) => {
  const items = R.sortBy(
    item => -Math.abs(parseInt(item.value)),
    payload?.map(item => {
      let { name, value } = item;
      name = R.defaultTo("", name);
      value = R.defaultTo(0, value as number);
      return {
        label: formatTooltipLabel(name),
        value: formatNumberAsInt(value),
        color: getTooltipColor(name),
      };
    }) || []
  );

  return (
    <ChartTooltipV2
      headers={[formatDateLabel(label, fiscalCal, dateGrouping, true)]}
      items={items}
      itemKeyShape="line"
    />
  );
};
